import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserCircleIcon,
  CogIcon,
  BellIcon,
  ShieldCheckIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  KeyIcon,
  GlobeAltIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CloudArrowDownIcon,
  InformationCircleIcon,
  ServerIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  ClockIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  TrashIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { agentsService } from '../services/agents';
import { workflowsService } from '../services/workflows';
import toast from 'react-hot-toast';

interface UserProfile {
  name: string;
  email: string;
  role: string;
  avatar: string;
  joinDate: string;
}

interface PlatformInfo {
  version: string;
  latestVersion: string;
  updateAvailable: boolean;
  lastUpdated: string;
  lastChecked: string;
  buildNumber: string;
  environment: string;
  region: string;
  uptime: string;
  autoUpdateEnabled: boolean;
}

const Settings: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'profile' | 'preferences' | 'security' | 'platform' | 'danger'>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [secretClickCount, setSecretClickCount] = useState(0);
  const [showDangerZone, setShowDangerZone] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  
  // Use real user profile data from auth context
  const [userProfile, setUserProfile] = useState<UserProfile>({
    name: user?.full_name || 'Unknown User',
    email: user?.email || 'unknown@example.com',
    role: user?.is_superuser ? 'Administrator' : 'User',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    joinDate: user?.created_at || new Date().toISOString()
  });

  // Update profile when user data changes
  useEffect(() => {
    if (user) {
      setUserProfile({
        name: user.full_name || 'Unknown User',
        email: user.email || 'unknown@example.com', 
        role: user.is_superuser ? 'Administrator' : 'User',
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
        joinDate: user.created_at || new Date().toISOString()
      });
      setEditProfile({
        name: user.full_name || 'Unknown User',
        email: user.email || 'unknown@example.com',
        role: user.is_superuser ? 'Administrator' : 'User', 
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
        joinDate: user.created_at || new Date().toISOString()
      });
    }
  }, [user]);

  // Database reset mutation - SECRET FUNCTIONALITY
  const resetDatabaseMutation = useMutation({
    mutationFn: async () => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      // First get all agents and workflows
      const agentsResponse = await agentsService.getAgents({ workspace_id: currentWorkspace.id, per_page: 1000 });
      const workflowsResponse = await workflowsService.getWorkflows(currentWorkspace.id, 1, 1000);
      
      const agents = agentsResponse.agents || [];
      const workflows = workflowsResponse.items || [];
      
      // Delete all workflows first (they might depend on agents)
      for (const workflow of workflows) {
        await workflowsService.deleteWorkflow(workflow.id);
      }
      
      // Then delete all agents
      for (const agent of agents) {
        await agentsService.deleteAgent(agent.id);
      }
      
      return { deletedAgents: agents.length, deletedWorkflows: workflows.length };
    },
    onSuccess: (data) => {
      toast.success(`🗑️ Reset complete! Deleted ${data.deletedAgents} agents and ${data.deletedWorkflows} workflows`);
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setResetConfirmText('');
      setShowDangerZone(false);
    },
    onError: (error: any) => {
      toast.error(`Reset failed: ${error.message}`);
    }
  });

  // Secret unlock mechanism
  const handleSecretClick = () => {
    setSecretClickCount(prev => prev + 1);
    if (secretClickCount + 1 >= 7) {
      setShowDangerZone(true);
      toast.success('🔓 Developer tools unlocked!');
    }
  };

  // Handle database reset
  const handleDatabaseReset = () => {
    if (resetConfirmText !== 'DELETE EVERYTHING') {
      toast.error('Please type "DELETE EVERYTHING" to confirm');
      return;
    }
    resetDatabaseMutation.mutate();
  };

  const [editProfile, setEditProfile] = useState<UserProfile>(userProfile);
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    workflow: true,
    security: true
  });

  const [platformInfo, setPlatformInfo] = useState<PlatformInfo>({
    version: '2.1.4',
    latestVersion: '2.2.0',
    updateAvailable: true,
    lastUpdated: '2024-12-15T10:30:00Z',
    lastChecked: '2024-12-20T14:22:00Z',
    buildNumber: 'build-2024.12.15.1043',
    environment: 'Production',
    region: 'US-East-1',
    uptime: '99.9%',
    autoUpdateEnabled: false
  });

  const handleSaveProfile = () => {
    setUserProfile(editProfile);
    setIsEditing(false);
    toast.success('Profile updated successfully');
  };

  const handleCancelEdit = () => {
    setEditProfile(userProfile);
    setIsEditing(false);
  };

  const handleCheckUpdates = async () => {
    setCheckingUpdates(true);
    // Simulate API call
    setTimeout(() => {
      setPlatformInfo(prev => ({
        ...prev,
        lastChecked: new Date().toISOString(),
        updateAvailable: Math.random() > 0.5 // Randomly determine if update is available
      }));
      setCheckingUpdates(false);
      toast.success('Update check completed');
    }, 2000);
  };

  const handleInstallUpdate = () => {
    toast.success('Update installation initiated. You will be notified when complete.');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getThemeIcon = (themeType: string) => {
    switch (themeType) {
      case 'light':
        return <SunIcon className="h-5 w-5" />;
      case 'dark':
        return <MoonIcon className="h-5 w-5" />;
      case 'system':
        return <ComputerDesktopIcon className="h-5 w-5" />;
      default:
        return <SunIcon className="h-5 w-5" />;
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: UserCircleIcon },
    { id: 'preferences', name: 'Preferences', icon: CogIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'platform', name: 'Platform', icon: ServerIcon },
    ...(showDangerZone ? [{ id: 'danger', name: '⚠️ Danger Zone', icon: TrashIcon }] : [])
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Settings</h1>
          <p className="text-gray-600 dark:text-gray-300">
            Manage your account settings and preferences
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Profile Card */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Profile Information
                    </h2>
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/30 hover:bg-indigo-200 dark:hover:bg-indigo-900/50 transition-colors duration-200"
                      >
                        <PencilIcon className="w-4 h-4 mr-2" />
                        Edit Profile
                      </button>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSaveProfile}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-200"
                        >
                          <CheckIcon className="w-4 h-4 mr-2" />
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                        >
                          <XMarkIcon className="w-4 h-4 mr-2" />
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="px-6 py-6">
                  <div className="flex items-center space-x-6 mb-6">
                    <div className="flex-shrink-0">
                      <img
                        className="h-20 w-20 rounded-full object-cover ring-4 ring-white dark:ring-gray-800"
                        src={userProfile.avatar}
                        alt={userProfile.name}
                      />
                    </div>
                    <div className="flex-1">
                      {!isEditing ? (
                        <>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                            {userProfile.name}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300">{userProfile.email}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {userProfile.role}
                          </p>
                        </>
                      ) : (
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={editProfile.name}
                            onChange={(e) => setEditProfile({ ...editProfile, name: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white text-lg font-semibold"
                            placeholder="Full Name"
                          />
                          <input
                            type="email"
                            value={editProfile.email}
                            onChange={(e) => setEditProfile({ ...editProfile, email: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                            placeholder="Email Address"
                          />
                          <select
                            value={editProfile.role}
                            onChange={(e) => setEditProfile({ ...editProfile, role: e.target.value })}
                            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
                          >
                            <option value="Administrator">Administrator</option>
                            <option value="Manager">Manager</option>
                            <option value="User">User</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <div className="flex items-center">
                      <UserCircleIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Member since</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {formatDate(userProfile.joinDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'preferences' && (
            <div className="space-y-6">
              {/* Theme Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Theme Settings
                  </h2>
                </div>
                <div className="px-6 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {['light', 'dark', 'system'].map((themeOption) => (
                      <button
                        key={themeOption}
                        onClick={() => setTheme(themeOption as any)}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          theme === themeOption
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="flex flex-col items-center space-y-2">
                          {getThemeIcon(themeOption)}
                          <span className="font-medium text-gray-900 dark:text-white capitalize">
                            {themeOption}
                          </span>
                          {theme === themeOption && (
                            <CheckIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Notification Preferences
                  </h2>
                </div>
                <div className="px-6 py-6">
                  <div className="space-y-4">
                    {Object.entries(notifications).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <BellIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                              {key} Notifications
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Receive {key} notifications about platform activity
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setNotifications(prev => ({ ...prev, [key]: !value }))}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            value ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              value ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              {/* Security Overview */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Security Overview
                  </h2>
                </div>
                <div className="px-6 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <div className="flex items-center">
                        <ShieldCheckIcon className="h-5 w-5 text-green-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">Account Security</p>
                          <p className="text-xs text-green-600 dark:text-green-400">Strong</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <div className="flex items-center">
                        <KeyIcon className="h-5 w-5 text-blue-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">API Keys</p>
                          <p className="text-xs text-blue-600 dark:text-blue-400">3 Active</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* API Keys */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      API Keys
                    </h2>
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200">
                      Generate New Key
                    </button>
                  </div>
                </div>
                <div className="px-6 py-6">
                  <div className="space-y-4">
                    {['Production API Key', 'Development API Key', 'Staging API Key'].map((keyName, index) => (
                      <div key={keyName} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <KeyIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{keyName}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              sk-...{Math.random().toString(36).substring(2, 8)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Last used: {index === 0 ? '2 hours ago' : index === 1 ? '1 day ago' : '1 week ago'}
                          </span>
                          <button className="text-red-600 hover:text-red-700 text-sm">
                            Revoke
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'platform' && (
            <div className="space-y-6">
              {/* Platform Information */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Platform Information
                  </h2>
                </div>
                <div className="px-6 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <div className="flex items-center">
                        <InformationCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">Current Version</p>
                          <p 
                            className="text-sm text-gray-600 dark:text-gray-300 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                            onClick={handleSecretClick}
                            title={secretClickCount > 0 ? `${7 - secretClickCount} clicks remaining` : 'Click me 7 times...'}
                          >
                            {platformInfo.version}
                            {secretClickCount > 0 && secretClickCount < 7 && (
                              <span className="ml-2 text-xs text-blue-500">({secretClickCount}/7)</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <div className="flex items-center">
                        <CalendarIcon className="h-5 w-5 text-blue-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">Last Updated</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {formatDateTime(platformInfo.lastUpdated)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Update Check */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Update Check
                    </h2>
                    <button
                      onClick={handleCheckUpdates}
                      className={`px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 ${
                        checkingUpdates ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      disabled={checkingUpdates}
                    >
                      {checkingUpdates ? 'Checking...' : 'Check for Updates'}
                    </button>
                  </div>
                </div>
                <div className="px-6 py-6">
                  <div className="space-y-4">
                    {Object.entries(platformInfo).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <InformationCircleIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                              {key}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {typeof value === 'boolean' ? value ? 'Yes' : 'No' : value}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Update Installation */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Update Installation
                    </h2>
                    <button
                      onClick={handleInstallUpdate}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
                    >
                      Install Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'danger' && (
            <div className="space-y-6">
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl shadow-sm border border-red-200 dark:border-red-700">
                <div className="px-6 py-4 border-b border-red-200 dark:border-red-700">
                  <h2 className="text-lg font-semibold text-red-900 dark:text-red-100">
                    Danger Zone
                  </h2>
                </div>
                <div className="px-6 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                      <div className="flex items-center">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-red-900 dark:text-red-100">
                            Database Reset
                          </p>
                          <p className="text-xs text-red-600 dark:text-red-400">
                            Permanently delete all agents and workflows in your workspace.
                            This action cannot be undone.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                      <div className="flex items-center">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-red-900 dark:text-red-100">
                            Account Deletion
                          </p>
                          <p className="text-xs text-red-600 dark:text-red-400">
                            Permanently delete your account and all associated data.
                            This action cannot be undone.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reset Confirmation */}
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4">
                <div className="flex items-center">
                  <TrashIcon className="h-5 w-5 text-red-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-red-900 dark:text-red-100">
                      Confirm Reset
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400">
                      Type "DELETE EVERYTHING" to confirm you want to reset your workspace.
                    </p>
                    <input
                      type="text"
                      value={resetConfirmText}
                      onChange={(e) => setResetConfirmText(e.target.value)}
                      className="mt-2 block w-full px-3 py-2 border border-red-300 dark:border-red-600 rounded-md shadow-sm placeholder-red-400 dark:placeholder-red-500 focus:outline-none focus:ring-red-500 focus:border-red-500 dark:bg-red-700 dark:text-white"
                      placeholder="Type 'DELETE EVERYTHING' to confirm"
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleDatabaseReset}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200"
                    disabled={resetConfirmText !== 'DELETE EVERYTHING' || resetDatabaseMutation.isPending}
                  >
                    {resetDatabaseMutation.isPending ? 'Resetting...' : 'Reset Workspace'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Settings; 